/**
 * How each tenant-configurable intake field is presented (migration 054).
 *
 * WHICH fields appear is tenant data, read from `GET /public/intake`. HOW a
 * field is presented is code, because an input type and an autocomplete token
 * are not a per-school decision. Wording lives here rather than in the tenant
 * content map because the two tenants that render this form today either
 * collect a field with these labels or do not collect it at all; a tenant that
 * later wants different wording for the same field overrides this one map.
 */
import type { IntakeFieldKey } from "@/lib/api/publicApply";

export type IntakeFieldGroup = "participant" | "guardian";

export interface IntakeFieldPresentation {
  autoComplete?: string;
  group: IntakeFieldGroup;
  helper?: string;
  hint?: string;
  label: string;
  maxLength: number;
  placeholder?: string;
  type: "text" | "tel" | "date";
  /** Wants a full row where the caller lays the form out in columns. */
  wide?: boolean;
}

export const INTAKE_FIELD_PRESENTATION: Record<IntakeFieldKey, IntakeFieldPresentation> = {
  date_of_birth: {
    autoComplete: "bday",
    group: "participant",
    label: "Date of birth",
    maxLength: 10,
    type: "date",
  },
  school_name: {
    group: "participant",
    label: "School / college / university",
    maxLength: 200,
    placeholder: "Where the participant currently studies",
    type: "text",
  },
  grade_level: {
    group: "participant",
    label: "Grade / year / semester",
    maxLength: 100,
    placeholder: "10th grade, sophomore year, second semester…",
    type: "text",
  },
  major: {
    group: "participant",
    hint: "college and university students only",
    label: "Major",
    maxLength: 200,
    type: "text",
    wide: true,
  },
  mother_name: {
    group: "guardian",
    label: "Mother’s full name",
    maxLength: 200,
    type: "text",
  },
  mother_phone: {
    autoComplete: "tel",
    group: "guardian",
    label: "Mother’s phone number",
    maxLength: 50,
    type: "tel",
  },
  mother_address: {
    group: "guardian",
    label: "Mother’s address",
    maxLength: 300,
    placeholder: "Street, city, state, ZIP code",
    type: "text",
    wide: true,
  },
  father_name: {
    group: "guardian",
    label: "Father’s full name",
    maxLength: 200,
    type: "text",
  },
  father_phone: {
    autoComplete: "tel",
    group: "guardian",
    label: "Father’s phone number",
    maxLength: 50,
    type: "tel",
  },
  father_address: {
    group: "guardian",
    helper: "Repeat the shared address if applicable.",
    label: "Father’s address",
    maxLength: 300,
    placeholder: "Street, city, state, ZIP code",
    type: "text",
    wide: true,
  },
};

export const PARTICIPANT_SECTION_HEADING = "Participant details";
export const GUARDIAN_SECTION_HEADING = "Parent / guardian information";
